import { DatePicker } from "@app/components/DatePicker.tsx";
import useSetupSystemInfoData from "@app/data-hooks/setup/use-setup-system-info-data.ts";
import { Box, Button, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { CurrencyInput } from "bi-lib";
import moment from "moment";
import { useMemo } from "react";

export default function SystemConfigurationForm() {
    const { systemInfo, saveSystemInfo } = useSetupSystemInfoData();

    const systemInfoData = useMemo(() => systemInfo.data?.[0], [systemInfo]);
    const defaultValues: any = {
        periodsPerMonth: systemInfoData?.periodsPerMonth,
        installationDate: systemInfoData?.installationDate ? moment(systemInfoData.installationDate) : null,
        expirationDate: systemInfoData?.expirationDate ? moment(systemInfoData.expirationDate) : null,
        reserveLowerBound: systemInfoData?.reserveLowerBound,
        reserveUpperBound: systemInfoData?.reserveUpperBound,
        maxInvoiceOffice: systemInfoData?.maxInvoiceOffice,
        maxInvoiceOtherIndiv: systemInfoData?.maxInvoiceOtherIndiv,
        maxInvoiceTime: systemInfoData?.maxInvoiceTime,
        maxInvoiceTravel: systemInfoData?.maxInvoiceTravel,
        maxOffice: systemInfoData?.maxOffice,
        maxOtherIndiv: systemInfoData?.maxOtherIndiv,
        maxTimeQuantity: systemInfoData?.maxTimeQuantity,
        maxTravel: systemInfoData?.maxTravel,
        maxYearInterval: systemInfoData?.maxYearInterval,
        fullTimeHoursPerDay: systemInfoData?.fullTimeHoursPerDay,
    };
    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            await saveSystemInfo.mutateAsync({
                periodsPerMonth: value.periodsPerMonth,
                installationDate: value.installationDate?.format("MM/DD/YYYY"),
                expirationDate: value.expirationDate?.format("MM/DD/YYYY"),
                reserveLowerBound: value.reserveLowerBound,
                reserveUpperBound: value.reserveUpperBound,
                maxInvoiceOffice: value.maxInvoiceOffice,
                maxInvoiceOtherIndiv: value.maxInvoiceOtherIndiv,
                maxInvoiceTime: value.maxInvoiceTime,
                maxInvoiceTravel: value.maxInvoiceTravel,
                maxOffice: value.maxOffice,
                maxOtherIndiv: value.maxOtherIndiv,
                maxTimeQuantity: value.maxTimeQuantity,
                maxTravel: value.maxTravel,
                maxYearInterval: value.maxYearInterval,
                fullTimeHoursPerDay: value.fullTimeHoursPerDay,
                companyName: systemInfoData?.companyName,
                address1: systemInfoData?.address1,
                address2: systemInfoData?.address2,
                city: systemInfoData?.city,
                stateCode: systemInfoData?.stateCode,
                zipCode: systemInfoData?.zipCode,
                telephone: systemInfoData?.telephone,
                fax: systemInfoData?.fax,
                email: systemInfoData?.email,
                federalTaxId: systemInfoData?.federalTaxId,
                version: systemInfoData?.version,
            });
        },
    });

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
        >
            <Box display={"flex"} mb={2} flexWrap={"wrap"} gap={2}>
                <form.Field name={"periodsPerMonth"}>
                    {(field) => (
                        <TextField
                            type={"number"}
                            label={"Periods per Month"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(+e.target.value)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"installationDate"}>
                    {(field) => (
                        <DatePicker
                            label={"Installation Date"}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"expirationDate"}>
                    {(field) => (
                        <DatePicker
                            label={"Expiration Date"}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"reserveLowerBound"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Client Reserve Lower Bound"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"reserveUpperBound"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Client Reserve Upper Bound"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxInvoiceOffice"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Max Invoice Office"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxInvoiceOtherIndiv"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Max Invoice Other Indiv"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxInvoiceTime"}>
                    {(field) => (
                        <TextField
                            type={"number"}
                            label={"Max Invoice Time"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(+e.target.value)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxInvoiceTravel"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Max Invoice Travel"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxOffice"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Max Office Expense"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxOtherIndiv"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Max Other Indiv"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxTimeQuantity"}>
                    {(field) => (
                        <TextField
                            type={"number"}
                            label={"Max Time Quantity"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(+e.target.value)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxTravel"}>
                    {(field) => (
                        <CurrencyInput
                            type={"number"}
                            label={"Max Travel Expense"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"maxYearInterval"}>
                    {(field) => (
                        <TextField
                            type={"number"}
                            label={"Max Year Interval"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(+e.target.value)}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"fullTimeHoursPerDay"}>
                    {(field) => (
                        <TextField
                            type={"number"}
                            label={"Full Time Hours per Day"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(+e.target.value)}
                            required
                        />
                    )}
                </form.Field>
            </Box>
            <Button variant={"contained"} disabled={saveSystemInfo.isPending} type={"submit"}>
                Save
            </Button>
        </form>
    );
}
